import { Typography, Skeleton, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton } from '@components/atoms';
import { ProjectCard } from '@components/organisms/ProjectCard';
import { useProjects } from '@hooks';
import { services } from '@common/data/services';
const { Title, Paragraph } = Typography;

export const LandingPage = () => {
  const navigate = useNavigate();
  const { projects } = useProjects();

  // Get the three most recent projects
  const getRecentProjects = () => {
    if (!projects.data) return [];
    
    // Sort projects by creation date (most recent first) and take first 3
    return [...projects.data]
      .sort((a, b) => {
        const dateA = new Date(a.createdAt || a.endDate || '').getTime();
        const dateB = new Date(b.createdAt || b.endDate || '').getTime();
        return dateB - dateA;
      })
      .slice(0, 3);
  };

  const recentProjects = getRecentProjects();

  return (
    <div>
      {/* Hero Section with Background Video */}
      <section className="w-full relative flex flex-col items-center justify-between md:h-[800px] h-[320px] overflow-hidden">
        <video 
          autoPlay 
          muted 
          loop 
          className="absolute w-full h-full object-cover opacity-80"
        >
          <source src="/assets/videos/bg-video.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="container mx-auto px-4 z-10 mt-auto pb-8 md:pb-0">
          <div className="flex flex-wrap justify-center gap-6 md:gap-20">
            <PrimaryButton 
              onClick={() => { navigate('/services'); window.scrollTo(0, 0); }}
              className="md:w-[200px] w-[140px] !text-sm md:!text-xl !h-[30px] md:!h-[45px]"
            >
              OUR SERVICES
            </PrimaryButton>
            <PrimaryButton 
              onClick={() => { navigate('/contact'); window.scrollTo(0, 0); }}
              className="md:w-[180px] w-[140px] !text-sm md:!text-xl !h-[30px] md:!h-[45px]"
            >CONTACT US
            </PrimaryButton>
          </div>
        </div>
        
        <div className="w-full bottom-0 relative overflow-hidden">
        <img 
          src="/assets/images/between-sections-down-1.png" 
          alt="" 
          className="w-full h-full object-cover"
        />
        </div>
      </section>

      {/* About Section */}
      <section className="w-full h-full bg-first-dark-gray">
        <div className="container mx-auto px-4">          
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="md:w-1/2">
              <div>
                <Title level={2} className="font-android !font-normal !text-white pb-4">
                ABOUT RUPPERT INC.
                </Title>
                <Paragraph className="font-barlow text-gray-300 text-justify mb-4 text-sm md:text-base">
                The company was founded in 2005 by three brothers, Josh, Nate and Tim Ruppert. And almost immediately began to take enough work to maintain 25 – 30 full time
                employees. They did a large variety of work including framing, excavation, commercial snow removal, interior and exterior remodels, defensible space, structural and ornamental steel fabrication and more.
                </Paragraph>
                <Paragraph className="font-barlow text-gray-300 text-justify mb-4 text-sm md:text-base">
                Nate and Tim mostly limited their operation of Ruppert, Inc to the general engineering and snow removal, with a few select residential general building projects included throughout the years. The maintenance and upkeep of the equipment consists of a 2 to 4 employee shop that does all the service and 
                repairs in house as well and custom fabrication of truck bodies and equipment implements. Ruppert, Inc runs an average of 90 summer employees and 10 to 20 off season/winter employees.
                </Paragraph>

                <div className="flex justify-center md:justify-start my-8 md:my-12">
                  <PrimaryButton
                    size="large" 
                    onClick={() => { navigate('/about'); window.scrollTo(0, 0); }}
                    className="w-fit"
                  >
                  LEARN MORE
                  </PrimaryButton>
                </div>
              </div>
            </div>
            
            <div className="md:w-1/2 flex items-center justify-center mb-8 md:mb-12">
              <div className="overflow-hidden">
                <img 
                  src="/assets/images/ruppert-about.png" 
                  alt="About Ruppert" 
                  className="w-full h-auto max-h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section without Learn More links*/}
      <section className="relative w-full h-full py-6">
        <div className="services-bg opacity-60 absolute inset-0 w-full h-full -z-1"></div>
        <div className="container relative mx-auto z-10">
          <Title level={2} className="font-android !font-normal !text-white text-center">
            OUR SERVICES
          </Title>          
          <Paragraph className="font-barlow text-gray-300 text-center max-w-3xl mx-auto text-sm md:text-base">
            We offer a comprehensive range of construction and excavation services to meet your project needs
          </Paragraph>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mx-4 md:mx-auto">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-first-dark-gray styled-card opacity-90 flex flex-col h-[240px] md:h-[260px] p-3"
                onClick={() => { navigate(`/services?service=${service.id}`); window.scrollTo(0, 0); }}
              >
                <div className="mb-3">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="w-12 h-12 md:w-16 md:h-16 bg-yellow-principal p-2 object-contain"
                  />
                </div>
                <Title level={4} className="font-android !font-normal !text-white text-lg md:text-xl">{service.title}</Title>
                <Paragraph className="font-barlow text-gray-300 text-xs md:text-sm mb-3">
                  {service.shortDescription}
                </Paragraph>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center mt-12 md:mt-12 md:mb-0 mb-20">
            <PrimaryButton 
              size="large" 
              onClick={() => { navigate('/services'); window.scrollTo(0, 0); }}
              className="w-auto"
            >
              LEARN MORE
            </PrimaryButton>
          </div>
        </div>
        {/* Divisor visual na parte inferior */}
        <div className="digi-bg-md absolute w-full inset-0 md:-bottom-110 -bottom-230 -z-1"></div>
      </section>
      
      {/* Contact Info Section */}
      <section className="bg-first-dark-gray w-full py-18">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            <div className="bg-zinc-900 text-center flex flex-col items-center p-8">
              <div className="mb-6">
                <img src="/assets/icons/phone-icon.svg" alt="Phone icon" className="h-auto"/>
              </div>
              <Title level={3} className="font-android !font-normal !text-white text-xl">CALL US</Title>
              <Paragraph className="font-barlow text-gray-300 text-base">
                +1 (530) 587-4509 <br />
                Monday - Friday: 7am - 5pm
              </Paragraph>
            </div>
            
            <div className="bg-zinc-900 text-center flex flex-col items-center p-8">
              <div className="mb-6">
                <img src="/assets/icons/mail-icon.svg" alt="Mail icon" className="h-auto"/>
              </div>
              <Title level={3} className="font-android !font-normal !text-white text-xl">EMAIL US</Title>
              <Paragraph className="font-barlow text-gray-300 text-base">
                contact@ruppertinc.com <br />
                We respond within 24h
              </Paragraph>
            </div>
            
            <div className="bg-zinc-900 text-center flex flex-col items-center p-8">
              <div className="mb-6">
                <img src="/assets/icons/location-icon.svg" alt="Location icon" className="h-auto"/>
              </div>
              <Title level={3} className="font-android !font-normal !text-white text-xl">VISIT US</Title>
              <Paragraph className="font-barlow text-gray-300 text-base">
                11168 W River St <br />
                Truckee, CA 96161
              </Paragraph>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="w-full relative bg-second-black-carbon py-12">
        <div className="container mx-auto px-4 relative z-10">
          <Title level={2} className="font-android !font-normal !text-white text-center pb-6">
            FEATURED PROJECTS
          </Title>
          
          {projects.isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-zinc-900 overflow-hidden p-6">
                  <Skeleton active paragraph={{ rows: 4 }} />
                </div>
              ))}
            </div>
          )}

          {projects.isError && (
            <div className="text-center py-8">
              <Title level={4} className="!text-white !font-['Android_101']">
                ERROR LOADING PROJECTS
              </Title>
              <Button
                onClick={() => projects.refetch()}
                className="mt-4 !bg-[#E3AF25] !text-black"
              >
                TRY AGAIN
              </Button>
            </div>
          )}

          {!projects.isLoading && !projects.isError && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
                {recentProjects.length > 0 ? (
                  recentProjects.map(project => (
                    <ProjectCard key={project.id} project={project} navigate={navigate} />
                  ))
                ) : (
                  <div className="col-span-3 text-center py-8">
                    <Title level={4} className="!text-white !font-['Android_101']">
                      NO PROJECTS FOUND
                    </Title>
                    <Paragraph className="text-gray-300 !font-['Barlow']">
                      We couldn't find any projects. Please check back later.
                    </Paragraph>
                  </div>
                )}
              </div>
              
              {recentProjects.length > 0 && (
                <div className="flex justify-center mt-12">
                  <PrimaryButton 
                    size="large" 
                    onClick={() => { navigate('/projects'); window.scrollTo(0, 0); }}
                  >
                    VIEW ALL PROJECTS
                  </PrimaryButton>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      
      {/* Project Sign Up Section */}
      <section className="w-full h-[350px] relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/assets/images/bg-ruppert-ready-to-start.png" 
            alt="" 
            className="w-full h-full object-cover"
          />         
        </div>        
        <div className="container text-center relative mx-auto px-4 z-20">
          <Title level={2} className="font-android !font-normal !text-white mt-12">
            READY TO START YOUR PROJECT?
          </Title>
          <Paragraph className="font-barlow text-gray-300 text-xl md:text-2xl max-w-3xl mx-auto pb-6">
            Contact us today to discuss your construction needs and get a free estimate
          </Paragraph>
          <PrimaryButton className="mb-8 md:mb-4"
            size="large"
            onClick={() => { navigate('/quote'); window.scrollTo(0, 0); }}
          >REQUEST A QUOTE
          </PrimaryButton>
        </div>        
        {/* Divisor visual na parte inferior */}
        <div className="digi-bg-md absolute w-full inset-0 -bottom-80">
        </div>
      </section>

      {/* Our Family of Brands Section */}
      <section className="bg-first-dark-gray w-full pb-12 md:pb-16">
        <div className="container mx-auto">
          <Title level={2} className="font-android !font-normal !text-white text-center pb-8 md:pb-16 pt-12 md:pt-8">
            OUR FAMILY OF BRANDS
          </Title>
          
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 pb-6">
            {/* Logos das marcas */}
            <a 
              href="https://ruppertinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/ruppert-logo.svg" 
                alt="Ruppert Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://estabrookinc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/estabrook-logo.svg" 
                alt="Estabrook Logo" 
                className="max-w-full max-h-full" 
              />
            </a>
            <a 
              href="https://modlink.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src="/assets/logos/modlink-logo.svg" 
                alt="Modlink Logo" 
                className="max-w-full max-h-full"
              />
            </a>
            <div className="w-[100px] h-[70px] md:w-[180px] md:h-[80px] flex items-center justify-center">
              <img 
                src="/assets/logos/snow-sync-logo.svg" 
                alt="Snow Sync Logo" 
                className="max-w-full max-h-full" 
              />
            </div>
            <div className="w-[100px] md:w-[150px] h-[70px] md:mt-2 flex items-center justify-center">
              <img 
                src="/assets/logos/asr-logo.svg" 
                alt="ASR Logo" 
                className="max-w-full max-h-full"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Official Merchandise Section */}
      {/* <section className="w-full py-20 bg-black">
        <div className="container mx-auto px-4">
          <Title level={2} className="!text-white text-center font-android pb-16">
            OFFICIAL MERCHANDISE
          </Title>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="flex flex-col justify-center">
              <Title level={3} className="!text-white mb-6 font-android">SHOP OUR COLLECTION</Title>
              <Paragraph className="text-gray-300 text-xl py-8" style={paragraphStyle}>
                Show your support with official merchandise from our family of brands. 
                From t-shirts and hoodies to hats and accessories, we've got gear for 
                every fan of Ruppert Inc., Estabrook Snow Removal, SnowSync, Mod-Link, 
                and Alpine Snow Removal.
              </Paragraph>
              <div>
                <Button 
                  size="large" 
                  onClick={() => navigate('/merch')}
                  className="!bg-[#E3AF25] hover-3d text-black border-none min-w-[180px] h-[50px] font-bold rounded-none"
                >
                  SHOP NOW
                </Button>
              </div>
            </div>
            
            <div className="bg-zinc-800 rounded-lg overflow-hidden w-5/6 mx-auto flex items-center justify-center">
              <img 
                src="/merch-preview.jpg" 
                alt="Ruppert Merchandise" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = 'https://placehold.co/600x400?text=Ruppert+Merchandise';
                }}
              />
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
}; 